import { useState } from 'react';
import { Flag, ShieldOff, ShieldCheck, MoreVertical } from 'lucide-react';
import { useGql } from '@ah/design';
import { BottomSheet } from '@ah/design/mobile';
import { useMutation } from '@tanstack/react-query';
import { ReportSheet } from './ReportSheet.tsx';
import { BLOCK_USER_M, UNBLOCK_USER_M } from '../shared/queries.ts';
import type { ReportTargetType } from '../shared/types.ts';

/**
 * Drop-in kebab for any page that shows another user's content. Tapping
 * opens a portaled bottom sheet with Report (always) + Block / Unblock
 * (when a `subjectUserId` is supplied).
 *
 * Designed to be passed straight to `<Header trailing={...} />` or
 * placed inline. Both the menu sheet and the Report sheet are rendered
 * via `createPortal` so they escape Header's backdrop-blur containing
 * block — without that the sheets dock to the header instead of the
 * viewport on iOS WebKit.
 */
export function ModerationMenu({
  targetType,
  targetId,
  subjectUserId,
  subjectName,
  isBlocked = false,
  onBlocked,
  onUnblocked,
  reportTitle,
}: {
  targetType: ReportTargetType;
  targetId: string;
  /** The human responsible for the content — enables "Block user". Omit
   *  to show only "Report" (e.g. for a Circle whose creator the viewer
   *  doesn't need to block separately). */
  subjectUserId?: string;
  subjectName?: string | null;
  isBlocked?: boolean;
  onBlocked?: () => void;
  onUnblocked?: () => void;
  reportTitle?: string;
}) {
  const gql = useGql();
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const blockMutation = useMutation({
    mutationFn: (humanId: string) => gql.request(BLOCK_USER_M, { humanId }),
    onSuccess: () => {
      setMenuOpen(false);
      onBlocked?.();
    },
  });
  const unblockMutation = useMutation({
    mutationFn: (humanId: string) => gql.request(UNBLOCK_USER_M, { humanId }),
    onSuccess: () => {
      setMenuOpen(false);
      onUnblocked?.();
    },
  });

  return (
    <>
      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        aria-label="More options"
        className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-foreground active:bg-accent"
      >
        <MoreVertical size={20} />
      </button>

      <BottomSheet open={menuOpen} onClose={() => setMenuOpen(false)} title={subjectName ?? 'Options'}>
      <button
        type="button"
        onClick={() => {
          setMenuOpen(false);
          setReportOpen(true);
        }}
        className="flex items-center gap-2 w-full px-4 py-3 text-sm"
      >
        <Flag size={14} /> Report
      </button>
      {subjectUserId && !isBlocked && (
        <button
          type="button"
          onClick={() => blockMutation.mutate(subjectUserId)}
          disabled={blockMutation.isPending}
          className="flex items-center gap-2 w-full px-4 py-3 text-sm text-destructive disabled:opacity-50"
        >
          <ShieldOff size={14} />
          {blockMutation.isPending
            ? 'Blocking…'
            : subjectName
              ? `Block ${subjectName.split(' ')[0]}`
              : 'Block user'}
        </button>
      )}
      {subjectUserId && isBlocked && (
        <button
          type="button"
          onClick={() => unblockMutation.mutate(subjectUserId)}
          disabled={unblockMutation.isPending}
          className="flex items-center gap-2 w-full px-4 py-3 text-sm disabled:opacity-50"
        >
          <ShieldCheck size={14} />
          {unblockMutation.isPending ? 'Unblocking…' : 'Unblock'}
        </button>
      )}
      </BottomSheet>

      <ReportSheet
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType={targetType}
        targetId={targetId}
        title={reportTitle}
      />
    </>
  );
}
